'use client';

import { Controller, useFormContext } from 'react-hook-form';
import { BadgeSelect } from '@/app/(main)/clients/create/components/BadgeSelect';
import RadioButton from '@/app/components/buttons/RadioButton';
import { TextArea } from '@/app/components/form/TextArea';
import { CLASS_PREFERENCE_OPTIONS, type ClassPreference, SEAT_PREFERENCES } from '@/types/clientsApiTypes';
import type { AllergiesFormValues } from './types';

const ALLERGIES_MAX_LENGTH = 300;

type EditAllergiesFormProps = {
  isRelated?: boolean;
};

export function EditAllergiesForm({ isRelated = false }: EditAllergiesFormProps) {
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<AllergiesFormValues>();
  const seat = watch('fly.seat');
  const allergies = watch('allergies') ?? '';
  const isCommercial = isRelated ? watch('fly.fly_private_aviation') !== 'yes' : watch('fly.fly_private_aviation') === 'no';
  const isPrivate = watch('fly.fly_private_aviation') === 'yes';
  const flyPrivateAviation = watch('fly.fly_private_aviation');

  return (
    <div className="grid grid-cols-1 gap-4">
      {isRelated && (
        <>
          <div className="flex items-center gap-6">
            <RadioButton
              label="Commercial"
              checked={flyPrivateAviation === 'no'}
              onChange={() => setValue('fly.fly_private_aviation', 'no', { shouldDirty: true, shouldValidate: true })}
            />
            <RadioButton
              label="Private aviation"
              checked={flyPrivateAviation === 'yes'}
              onChange={() => setValue('fly.fly_private_aviation', 'yes', { shouldDirty: true, shouldValidate: true })}
            />
          </div>

          {isPrivate && (
            <TextArea
              {...register('fly.preferred_private_aviation_company')}
              disableResize
              label="Preferred private aviation company"
              placeholder="Please provide any relevant information we should know about your private aviation preferences."
              rows={4}
              errors={errors}
            />
          )}
          {isCommercial && (
            <>
              <div>
                <p className="text-body-small text-blue-900 mb-3">Seat Preference</p>
                <div className="flex items-center gap-6">
                  <RadioButton
                    label="Window"
                    checked={seat === SEAT_PREFERENCES.window}
                    onChange={() => setValue('fly.seat', SEAT_PREFERENCES.window, { shouldDirty: true, shouldValidate: true })}
                  />
                  <RadioButton
                    label="Aisle"
                    checked={seat === SEAT_PREFERENCES.aisle}
                    onChange={() => setValue('fly.seat', SEAT_PREFERENCES.aisle, { shouldDirty: true, shouldValidate: true })}
                  />
                </div>
              </div>
              <div>
                <Controller
                  name="fly.class_preference"
                  control={control}
                  render={({ field }) => (
                    <BadgeSelect<ClassPreference>
                      label="Class Preference"
                      type="secondary"
                      hint="Select all that apply"
                      options={CLASS_PREFERENCE_OPTIONS}
                      value={(field.value as ClassPreference[]) ?? []}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </>
          )}
        </>
      )}

      <TextArea<AllergiesFormValues>
        {...register('allergies')}
        disableResize
        label="Allergies & dietary restrictions"
        placeholder="e.g. nut allergy, gluten-free, lactose intolerance"
        rows={6}
        size="flexible_height"
        maxLength={ALLERGIES_MAX_LENGTH}
        value={allergies}
        errors={errors}
      />
    </div>
  );
}